// Copyright (c) StrangeBee 2024

import React, { ReactElement } from 'react';
import { Tag } from 'antd';
import { dateOperator, useLocalMessage, UseLocalMessageType } from '@Helpers/';
import { DescribeFieldOutput, getDateRange } from 'thehive-sdk';
import _ from 'lodash';
import { FormatDate } from '@Molecules/Format';

import './FilterPreview.scss';

interface ValueFormatterType {
    operator?: string;
    field?: string;
    values?: any[];
}

interface DependencyType {
    local: UseLocalMessageType;
    describeField?: DescribeFieldOutput;
}

const forceTypeArray = (values: unknown | any[]): any[] => (!_.isArray(values) ? [values] : values);

/**
 * collection of Formatter by type
 */
export type FilterFormatter = Record<
    string,
    (filter: ValueFormatterType, dep: DependencyType) => ReactElement | string
>;

const valueFormatter: FilterFormatter = {
    date: ({ operator = 'custom', values = [] }: ValueFormatterType): ReactElement | string => {
        let [from, to] = values || [null, null];
        const label = dateOperator[operator];
        if (operator !== 'custom') {
            [from, to] = getDateRange(operator);
        }
        if (from && to)
            return (
                <>
                    <FormatDate label={label + ' '} date={from} />
                    {' -> '}
                    <FormatDate date={to} />
                </>
            );
        if (from) return <FormatDate label={label} date={from} />;
        return label;
    },

    string: ({ values = [], operator }: ValueFormatterType): string => {
        return operator == 'eq' || !operator ? `${values}` : `${operator}(${values})`;
    },

    boolean: ({ operator = '' }: ValueFormatterType): string => {
        return operator;
    },

    number: (
        { operator = '', field = '', values = [] }: ValueFormatterType,
        { describeField }: DependencyType,
    ): string => {
        const valuesUser = forceTypeArray(values);
        const { labels = [], values: valuesRef = [] } =
            describeField?.attributes.find(({ name }) => name === field) || {};
        const rightValues = valuesUser.map((num) => {
            const valuesParsed = valuesRef.indexOf(parseInt(num));
            return valuesParsed !== -1 ? valuesParsed : num;
        });
        const listValue = rightValues.map((num) => labels[num] || num);

        return `${operator}(${listValue.join(', ')})`;
    },

    default: ({ values = [], operator }: ValueFormatterType): string => {
        return operator == 'eq' || !operator ? `${values}` : `${operator}(${values})`;
    },
};

interface FilterPreviewProps {
    onClose?: (...args: any[]) => any;
    describeField?: DescribeFieldOutput;
    formatter?: FilterFormatter;
    data: {
        field?: string;
        type?: string;
        operator?: string;
        values?: string[];
    };
}

export const FilterPreview = React.memo(
    ({
        onClose,
        describeField,
        formatter = {},
        data: { type = 'default', ...rest },
    }: FilterPreviewProps): ReactElement => {
        const local = useLocalMessage();

        return (
            <Tag closable={!!onClose} onClose={onClose} className="filter-preview">
                <b>{rest.field}:</b>{' '}
                {(formatter[type] || valueFormatter[type] || valueFormatter.default)(rest, {
                    local,
                    describeField,
                })}
            </Tag>
        );
    },
);
